import { RefObject, useLayoutEffect, useRef } from 'react'

// Define a generic type for the hook that extends HTMLElement to ensure it can be used with any HTML element
function useDynamicHeight<T extends HTMLElement>(
  dependencies?: any[]
): RefObject<T> {
  const ref = useRef<T | null>(null)
  const observerRef = useRef<MutationObserver | null>(null)
  const element = ref.current

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (element) {
        element.style.setProperty('--open-height', `${element.scrollHeight}px`)
        element.style.setProperty('--close-height', `${element.clientHeight}px`)
      }
    }

    if (element && !observerRef.current) {
      const config: MutationObserverInit = {
        childList: true,
        subtree: true,
        characterData: true
      }

      observerRef.current = new MutationObserver((mutationsList, observer) => {
        updateHeight()
      })

      observerRef.current.observe(element, config)

      updateHeight()
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (element) {
        element.style.removeProperty('--open-height')
        element.style.removeProperty('--close-height')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, dependencies || undefined])

  return ref
}

export default useDynamicHeight
